import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';


const navLinks = [
  { path: '/', key: 'nav_home' },
  { path: '/requests', key: 'nav_requests' },
  { path: '/beats', key: 'nav_beats' },
  { path: '/izbirak', key: 'nav_izbirak' },
  { path: '/portfolio', key: 'nav_portfolio' },
  { path: '/about', key: 'nav_about' },
  { path: '/contact', key: 'nav_contact' },
];

export default function Header() {
  const { t, lang, toggleLang } = useLanguage();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      <header
        data-testid="main-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled
          ? 'py-3  backdrop-blur-xl border-b'
          : 'py-5 bg-transparent'
          }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <img
              src={`${process.env.PUBLIC_URL}/images/logo.png`}
              alt="Zateviss"
              className="w-full h-8 rounded-full group-hover:scale-110 transition-transform duration-300"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map(link => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.key}`}
                  className={`relative px-4 py-2 text-sm font-medium transition-colors duration-200 ${isActive ? 'text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                >
                  {t(link.key)}
                  {isActive && (
                    <motion.div
                      layoutId="nav-indicator"
                      className="absolute bottom-0 left-3 right-3 h-[1px] rounded-full"
                      style={{ background: 'linear-gradient(90deg, #ffffff, #ffffff)' }}
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Right Controls */}
          <div className="flex items-center gap-3">
            <button
              data-testid="language-toggle"
              onClick={toggleLang}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] transition-colors duration-200 text-sm font-medium"
              aria-label={`Switch language to ${lang === 'tr' ? 'English' : 'Türkçe'}`}
            >
              <Globe className="w-3.5 h-3.5 text-white/50" />
              <span className="font-mono text-xs tracking-wider">{lang === 'tr' ? 'EN' : 'TR'}</span>
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-full bg-white/[0.03] hover:bg-white/[0.08] transition-colors duration-200"
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-[#050505]/98 backdrop-blur-2xl pt-24 px-6 lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col gap-1">
              {navLinks.map((link, i) => {
                const isActive = location.pathname === link.path;
                return (
                  <motion.div
                    key={link.path}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={link.path}
                      data-testid={`mobile-nav-link-${link.key}`}
                      className={`block px-4 py-5 text-2xl font-syne font-bold border-b border-white/[0.04] transition-colors duration-200 ${isActive ? 'text-white' : 'text-white/30 hover:text-white/60'
                        }`}
                    >
                      {t(link.key)}
                    </Link>
                  </motion.div>
                );
              })}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
